/**
 * Phase 3c selector additions — the handful of derivations `store/selectors.ts`
 * left to the UI lane because the prototype computed them inline in
 * `renderVals()` rather than in a view-model block.
 *
 * Kept in a separate file so the P3c lane never edits the shared `selectors.ts`
 * (four lanes are porting screens in parallel). Everything here is pure except
 * `removeChip`, which is a dispatcher: it takes the whole store because the
 * ✕ on a filter chip maps to a *different* existing action per field, and that
 * mapping is shared by all five screens that render the chip rail.
 */

import { SORT_OPTS } from '../lib/logic.ts';
import type { Filters, GameId, SortMode } from '../lib/types.ts';
import type { FilterChip } from './selectors.ts';
import type { QdStore } from './types.ts';

/** Prototype line 2543 — the five `SORT:` options, in order. */
export const SORT_OPTIONS: ReadonlyArray<{ v: SortMode; t: string }> = SORT_OPTS;

export interface SegState {
  bd: string;
  clr: string;
  bg: string;
}

/** Prototype `onOff` (line 2244). */
export function onOffSeg(on: boolean): SegState {
  return {
    bd: on ? 'var(--acc)' : 'var(--gt-border-2)',
    clr: on ? 'var(--acc)' : 'var(--gt-muted)',
    bg: on ? 'var(--accSoft)' : 'transparent',
  };
}

/** The ▦ / ≡ segmented pair on Discover — prototype `gSt` / `lSt` (line 2415). */
export function viewSeg(view: 'g' | 'l'): { g: SegState; l: SegState } {
  return { g: onOffSeg(view === 'g'), l: onOffSeg(view === 'l') };
}

/**
 * PORTING-NOTES D-02. A CheapShark-only row carries the minted `'cs<gameID>'`
 * id, which cannot satisfy the `list_entries.game_appid` foreign key — so the
 * row stays visible in search results but its quick-add is inert.
 */
export const NO_STEAM_TIP = "NO STEAM PAGE — CAN'T ADD YET";

export function quickAddBlocked(id: GameId): boolean {
  return typeof id !== 'number' || id <= 0;
}

/** Steam appids behind a set of result ids, for `queueAssets` (§5.3 batching). */
export function appidsOf(s: QdStore, ids: readonly GameId[]): number[] {
  const out: number[] = [];
  for (const id of ids) {
    const g = s.gById(id);
    if (g && g.appid > 0) out.push(g.appid);
  }
  return out;
}

/**
 * The ✕ on an active-filter chip. `FilterChip.field` / `.value` name the filter
 * the chip stands for (`selectors.ts#filterChips`); each one already has an
 * action on the ui slice, so this is a routing table, not new state logic.
 */
export function removeChip(s: QdStore, chip: FilterChip): void {
  switch (chip.field) {
    case 'p':
      s.setFlt({ p: 0 });
      return;
    case 'pr':
      s.setFlt({ pr: [0, 60] });
      return;
    case 'st':
      s.setStatusFilter(chip.value as keyof Filters['st'], 'any');
      return;
    case 'disc':
      s.setFlt({ disc: 0 });
      return;
    case 'rs':
      s.setFlt({ rs: 0 });
      return;
    case 'pt':
      s.setFlt({ pt: 'any' });
      return;
    // Tag chips share this rail since the picker moved into the filter panel.
    // Remove entirely, never cycle: a rail ✕ means "get rid of it", and routing
    // it through `cycleTag` would turn an include's ✕ into an exclude — the
    // opposite of what was asked.
    case 'tag':
      s.removeTag(Number(chip.value));
      return;
    // OWNED BY is one chip per member, so its ✕ drops that member alone rather
    // than the section — `toggleOwn` is already the "remove if present" action
    // and the member is present by construction if a chip is drawn for them.
    case 'own':
      s.toggleOwn(chip.value);
      return;
  }
}
